/**
 * Color Utilities
 *
 * Utility functions for color manipulation.
 */
import { logger } from '@/lib/logger';

/**
 * Derive three gradient stops from an accent color and return a CSS linear-gradient string.
 * Stops: light (lighten 30 %, alpha 0.6), dark (darken 20 %, alpha 0.8), base (alpha 0.7).
 */
export function accentGradientStops(accentColor: string): string {
  const light = lightenColor(accentColor, 30);
  const dark = darkenColor(accentColor, 20);

  const lightStop = colorToRgba(light, 0.6);
  const darkStop = colorToRgba(dark, 0.8);
  const baseStop = colorToRgba(accentColor, 0.7);

  return `linear-gradient(135deg, ${lightStop}, ${darkStop}, ${baseStop})`;
}

/**
 * Darken a hex color by a percentage
 * @param hex - Hex color string (e.g., '#D8F4D8')
 * @param percent - Percentage to darken (0-100)
 * @returns Darkened hex color string
 */
export function darkenColor(hex: string, percent: number): string {
  // Remove # if present
  const color = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Darken by reducing RGB values
  const factor = 1 - percent / 100;
  const darkenedR = Math.max(0, Math.floor(r * factor));
  const darkenedG = Math.max(0, Math.floor(g * factor));
  const darkenedB = Math.max(0, Math.floor(b * factor));

  // Convert back to hex
  return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB
    .toString(16)
    .padStart(2, '0')}`;
}

/**
 * Returns black or white depending on which has better contrast against the given color.
 * Uses the WCAG relative luminance formula.
 *
 * A color {@link parseColor} cannot read gets black: CSS will most often have
 * rejected it too, leaving the surface on the widget's white card, and black is
 * the readable choice there. Neither constant is right for a color we decline to
 * read, which is why the parser covers the formats a theme value arrives in
 * rather than either fallback being tuned.
 */
export function readableColor(color: string): '#000' | '#fff' {
  const luminance = relativeLuminance(color);
  if (luminance === null) {
    warnUnreadableColor(color);
    return '#000';
  }

  return luminance > 0.179 ? '#000' : '#fff';
}

/**
 * Whether {@link readableColor} / {@link readableIconColor} can derive a
 * foreground from this color rather than falling back to a fixed one.
 *
 * For a surface that carries our own text, this is the difference between a
 * derived pair and a guess: a fill CSS paints but this cannot read (a keyword, an
 * `oklch()`) would take whichever fixed foreground the caller has, which is wrong
 * for half of them. Such a surface should paint a fill it can pair with instead.
 */
export function canDeriveForeground(color: string): boolean {
  return relativeLuminance(color) !== null;
}

/**
 * Whether this color clears WCAG 1.4.11's 3:1 non-text floor against white, and
 * is therefore safe to paint on one of our white surfaces.
 *
 * False for a color {@link relativeLuminance} cannot read. That is the opposite
 * of {@link readableIconColor}'s assumption, and deliberate: this answers "may
 * this color be painted", where an unreadable value must not be waved through.
 */
export function hasNonTextContrastOnWhite(color: string): boolean {
  const luminance = relativeLuminance(color);
  if (luminance === null) {
    // Only the unreadable branch warns: a brand whose accent really is white is
    // configured correctly and simply fails the floor.
    warnUnreadableColor(color);
    return false;
  }
  return 1.05 / (luminance + 0.05) >= 3;
}

/**
 * Foreground for an icon drawn on the given background. Prefers white — the
 * widget's treatment on a brand fill — and only falls back to black once white
 * drops under the 3:1 floor WCAG sets for icons (1.4.11), which is what makes a
 * white glyph vanish on a pale brand color.
 */
export function readableIconColor(color: string): '#000' | '#fff' {
  const luminance = relativeLuminance(color);
  if (luminance === null) {
    warnUnreadableColor(color);
    return '#fff';
  }
  // Contrast ratio against white, whose relative luminance is 1.
  return 1.05 / (luminance + 0.05) >= 3 ? '#fff' : '#000';
}

/**
 * Lighten a hex color by a percentage.
 * Interpolates each RGB channel toward 255.
 * @param hex - Hex color string (e.g., '#8C69F0')
 * @param percent - Percentage to lighten (0-100)
 * @returns Lightened hex color string
 */
export function lightenColor(hex: string, percent: number): string {
  const color = hex.replace('#', '');

  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  const factor = percent / 100;
  const lightenedR = Math.min(255, Math.round(r + (255 - r) * factor));
  const lightenedG = Math.min(255, Math.round(g + (255 - g) * factor));
  const lightenedB = Math.min(255, Math.round(b + (255 - b) * factor));

  return `#${lightenedR.toString(16).padStart(2, '0')}${lightenedG.toString(16).padStart(2, '0')}${lightenedB
    .toString(16)
    .padStart(2, '0')}`;
}

/**
 * Returns the color unless it would be invisible against a white surface, in
 * which case the fallback is used. Mirrors V1's `getVisibleColor`
 * (web/src/lib/colorLogic.js), which guards a white brand color from producing a
 * white-on-white border. Anything `parseColor` cannot read — a CSS keyword, a
 * modern color space — also takes the fallback, which is the safe direction for
 * a border.
 */
export function getVisibleColor(color: string, fallbackColor: string): string {
  const rgb = parseColor(color);
  if (!rgb) {
    return fallbackColor;
  }
  return rgb.r === 255 && rgb.g === 255 && rgb.b === 255 ? fallbackColor : color;
}

/**
 * Like `colorToRgba`, but returns the fallback rather than a string containing
 * NaN when the color can't be parsed.
 *
 * The distinction matters wherever the result becomes a CSS custom property: an
 * `rgba(NaN, NaN, NaN, 0.1)` value still *defines* the variable, so a
 * `var(--x, fallback)` consumer never reaches its fallback and the declaration
 * instead becomes invalid at computed-value time — which resets the property to
 * its initial value rather than leaving it alone.
 */
export function colorToRgbaOr(color: string, opacity: number, fallback: string): string {
  const rgba = colorToRgba(color, opacity);
  return rgba.includes('NaN') ? fallback : rgba;
}

/**
 * Convert color to rgba with opacity
 * Handles both hex (#RRGGBB) and rgba formats
 */
export function colorToRgba(color: string, opacity: number): string {
  // If already rgba format, extract RGB values and apply new opacity
  const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbaMatch) {
    const r = rgbaMatch[1];
    const g = rgbaMatch[2];
    const b = rgbaMatch[3];
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // Handle hex format
  const cleanHex = color.replace('#', '');

  // Parse RGB values
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/** WCAG relative luminance, or null when the color isn't one we can read. */
export function relativeLuminance(color: string): number | null {
  const rgb = parseColor(color);
  if (!rgb) {
    return null;
  }
  const toLinear = (c: number) => {
    const channel = c / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
}

interface Rgb {
  b: number;
  g: number;
  r: number;
}

/**
 * The formats a configured theme color actually arrives in: hex (3/4/6/8 digits),
 * `rgb()`/`rgba()` and `hsl()`/`hsla()`, in either comma or space syntax, with
 * numbers or percentages.
 *
 * Alpha is **composited over white**, not discarded: the widget's surfaces are
 * drawn on its white card, so a translucent fill paints lighter than its channels
 * say and a foreground derived from the channels alone can be unreadable —
 * `rgba(0, 0, 0, 0.1)` reads as black but paints near-white.
 *
 * What CSS rejects, this rejects: a bare `000000`, a mixed comma/space syntax, an
 * alpha token that is not a number or a percentage. Those values never paint, so
 * reading one would derive a foreground against a fill that isn't there.
 *
 * Deliberately not exhaustive. CSS keywords (`black`) and the modern spaces
 * (`oklch()`, `color()`) return null, and callers then take a fixed foreground
 * rather than guessing from a color they cannot read. Widening this is what fixes
 * such a case — not retuning a fallback, which is only ever right for half the
 * inputs it covers.
 */
function parseColor(value: string): Rgb | null {
  const input = value.trim().toLowerCase();

  const fromHex = parseHex(input);
  if (fromHex) {
    return fromHex;
  }

  const fn = /^(rgba?|hsla?)\(([^)]*)\)$/.exec(input);
  if (!fn) {
    return null;
  }
  const parts = splitArguments(fn[2]);
  if (!parts) {
    return null;
  }
  const alpha = parts.length === 4 ? alphaValue(parts[3]) : 1;
  if (alpha === null) {
    return null;
  }

  if (fn[1].startsWith('rgb')) {
    const [r, g, b] = parts.slice(0, 3).map(rgbChannel);
    return r === null || g === null || b === null ? null : overWhite({ r, g, b }, alpha);
  }

  const hue = number(parts[0].replace(/deg$/, ''));
  const saturation = percentage(parts[1]);
  const lightness = percentage(parts[2]);
  if (hue === null || saturation === null || lightness === null) {
    return null;
  }
  return overWhite(hslToRgb(hue, saturation, lightness), alpha);
}

/**
 * Three channels plus an optional alpha, in one syntax or the other — never a mix.
 * CSS takes either the legacy comma form (`16, 16, 16` / `16, 16, 16, 0.5`) or the
 * modern space form (`16 16 16` / `16 16 16 / 50%`) and rejects a value that
 * blends them, so a lenient split would read a color the page never paints.
 */
function splitArguments(raw: string): string[] | null {
  const trimmed = raw.trim();

  if (trimmed.includes(',')) {
    if (trimmed.includes('/')) {
      return null;
    }
    const parts = trimmed.split(',').map((part) => part.trim());
    return withinArity(parts) && parts.every((part) => part !== '' && !/\s/.test(part)) ? parts : null;
  }

  const [channels, alpha, ...extra] = trimmed.split('/').map((part) => part.trim());
  if (extra.length || (alpha !== undefined && (alpha === '' || /\s/.test(alpha)))) {
    return null;
  }
  const parts = channels.split(/\s+/).filter(Boolean);
  if (alpha !== undefined) {
    parts.push(alpha);
  }
  return withinArity(parts) ? parts : null;
}

function withinArity(parts: string[]): boolean {
  return parts.length === 3 || parts.length === 4;
}

/** `0.5` or `50%` as 0–1. */
function alphaValue(part: string): number | null {
  if (part.endsWith('%')) {
    return percentage(part);
  }
  const value = number(part);
  return value === null ? null : clamp(value, 0, 1);
}

/**
 * The color as painted: over the widget's white card. The launcher also sits on
 * the host page, whose backdrop we can't know — white is the better assumption
 * there than treating a translucent fill as opaque.
 */
function overWhite(rgb: Rgb, alpha: number): Rgb {
  if (alpha >= 1) {
    return rgb;
  }
  const composite = (channel: number) => alpha * channel + (1 - alpha) * 255;
  return { r: composite(rgb.r), g: composite(rgb.g), b: composite(rgb.b) };
}

const NUMBER_PATTERN = /^[+-]?(\d+\.?\d*|\.\d+)$/;

function number(part: string): number | null {
  return NUMBER_PATTERN.test(part) ? Number.parseFloat(part) : null;
}

/** 0–255, from either `16` or `6%`. */
function rgbChannel(part: string): number | null {
  if (part.endsWith('%')) {
    const fraction = percentage(part);
    return fraction === null ? null : clamp(fraction * 255, 0, 255);
  }
  const value = number(part);
  return value === null ? null : clamp(value, 0, 255);
}

/** A `50%` string as 0–1. */
function percentage(part: string): number | null {
  if (!part.endsWith('%')) {
    return null;
  }
  const value = number(part.slice(0, -1));
  return value === null ? null : clamp(value / 100, 0, 1);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const hue = ((h % 360) + 360) % 360;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const second = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const match = l - chroma / 2;
  const [r, g, b] =
    hue < 60
      ? [chroma, second, 0]
      : hue < 120
        ? [second, chroma, 0]
        : hue < 180
          ? [0, chroma, second]
          : hue < 240
            ? [0, second, chroma]
            : hue < 300
              ? [second, 0, chroma]
              : [chroma, 0, second];
  return { r: (r + match) * 255, g: (g + match) * 255, b: (b + match) * 255 };
}

/**
 * 3, 4, 6 or 8 digits behind a `#`, with the alpha pair composited: see
 * {@link parseColor}. The `#` is required because these values also go straight
 * into CSS, which rejects a bare `000000` — reading one would derive a foreground
 * for a fill that never paints.
 */
function parseHex(value: string): Rgb | null {
  if (!value.startsWith('#')) {
    return null;
  }
  const clean = value.slice(1);
  const digits = /^[\da-f]{6}([\da-f]{2})?$/.test(clean)
    ? clean
    : /^[\da-f]{3}[\da-f]?$/.test(clean)
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : null;
  if (!digits) {
    return null;
  }
  const rgb = {
    r: parseInt(digits.substring(0, 2), 16),
    g: parseInt(digits.substring(2, 4), 16),
    b: parseInt(digits.substring(4, 6), 16),
  };
  const alpha = digits.length === 8 ? parseInt(digits.substring(6, 8), 16) / 255 : 1;
  return overWhite(rgb, alpha);
}

// Once per distinct value: these run per render, and an org's theme is one value.
// Diagnostic only — a configured color reached a reader that could not parse it.
// The fix is a corrected config, or widening parseColor when the value is CSS
// this cannot yet read (a keyword, an oklch()); the message names what it takes
// so the reader can tell which.
const warnedColors = new Set<string>();

function warnUnreadableColor(color: string): void {
  if (warnedColors.has(color)) {
    return;
  }
  warnedColors.add(color);
  logger.warn(
    `[theme] cannot read the color "${color}" — expected #hex, rgb()/rgba() or hsl()/hsla(); using a fixed value instead`,
  );
}
